import type { Organization, Survey } from '@engagement-web-app/common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    createFileRoute,
    type ErrorComponentProps,
    Outlet,
    SearchParamError,
    stripSearchParams,
    useLocation,
} from '@tanstack/react-router';
import classNames from 'classnames';
import { Building2, LayoutGrid, List, MessageCircleMore } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ErrorOverlay } from '@/components/ErrorOverlay/index.js';
import { Flex } from '@/components/Flex';
import { Form } from '@/components/Form';
import { InvalidRequest } from '@/components/InvalidRequest';
import { Page } from '@/components/Page';
import { Pill } from '@/components/Pill';
import { RouteError } from '@/components/RouteError';
import { Section, SectionHeader } from '@/components/Section';
import { Select } from '@/components/Select';
import { RouteTabs } from '@/components/Tabs';
import { showErrorToast, showSuccessToast } from '@/components/Toasts';
import { fetchOrganizations } from '@/queries/organizations';
import { fetchSurveys, updatePublishStatus } from '@/queries/surveys/system';
import { hiddenInPrint } from '@/styles/print.css';
import { UpdatePublishStatusDialog } from './-fragments/UpdatePublishStatusDialog.js';
import { DefaultRouteQueryParams, validateSearch } from './-search/index.js';
import * as styles from './route.css';

export const Route = createFileRoute('/system/_auth/surveys')({
    component: SystemSurveysRoute,
    loader: async () => {
        // TODO: Avoid hard-coded limit by implementing more user-friendly component
        const data = await fetchOrganizations({ limit: 100 });
        return { organizations: data.list };
    },
    validateSearch,
    search: {
        middlewares: [stripSearchParams(DefaultRouteQueryParams)],
    },
    pendingComponent: SystemSurveysRouteSkeleton,
    errorComponent: SystemSurveysRouteError,
});

function SystemSurveysRouteError({ error, reset }: ErrorComponentProps) {
    // `validateSearch` throwing (e.g. a malformed param failing `schema.parse()`) gets
    // caught by the router and re-thrown as its own `SearchParamError` — the original
    // error survives only as `.cause`, so that's what has to be checked here, not the
    // validator's own error type. Distinct from `fetchOrganizations` (the loader)
    // failing, which calls for a retry rather than navigating away from a URL that was
    // never wrong to begin with.
    if (error instanceof SearchParamError) {
        return <InvalidRequest title="サーベイ分析" kind="system" />;
    }

    return <RouteError kind="system" title="サーベイ分析" onRetry={reset} />;
}

function SystemSurveysRoute() {
    const navigate = Route.useNavigate();
    const { organizations } = Route.useLoaderData();
    const search = Route.useSearch();
    const location = useLocation();
    const [selectedOrgId, setSelectedOrgId] = useState<string>('');
    const [selectedSurveyId, setSelectedSurveyId] = useState<string>('');

    useEffect(() => {
        setSelectedOrgId(search.organizationId);
        setSelectedSurveyId(search.surveyId);
    }, [search]);

    const surveysQuery = useQuery({
        queryKey: ['surveys', selectedOrgId],
        queryFn: () => fetchSurveys(selectedOrgId),
        enabled: selectedOrgId !== '',
    });

    const selectedOrg = organizations.find((org) => org.uuid === selectedOrgId);
    const surveys = surveysQuery.data?.surveys ?? null;
    const selectedSurvey = surveys?.find((s) => s.surveyId === selectedSurveyId);

    const handleOrgChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedOrgId(e.target.value);
        setSelectedSurveyId('');
        navigate({ to: location.pathname, search: { organizationId: e.target.value, surveyId: '' } });
    };

    const handleSurveyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedSurveyId(e.target.value);
        navigate({
            to: location.pathname,
            search: { organizationId: search.organizationId, surveyId: e.target.value },
        });
    };

    return (
        <Page.Root>
            <Page.Header title="サーベイ分析" />

            <Page.Content>
                <Section pt="8" className={styles.selectionSection}>
                    <SectionHeader
                        title="クライアントと​サーベイ選択"
                        description="分析対象のクライアントとサーベイを選択します。クライアントを選ぶと、そのクライアントに紐づくサーベイが一覧に表示されます。ここで選んだサーベイの集計結果が、以降のすべてのセクションに反映されます。"
                    />

                    <Flex direction="column" gap="6">
                        <Flex gap="4">
                            <Form.Field
                                className={styles.selectWrapper}
                                label="クライアント選択"
                                control={(...props) => (
                                    <Select {...props} value={selectedOrgId} onChange={handleOrgChange}>
                                        <option value="">--クライアントを選択してください--</option>
                                        {organizations.map((org) => (
                                            <option key={org.uuid} value={org.uuid}>
                                                {org.name}
                                            </option>
                                        ))}
                                    </Select>
                                )}
                            />

                            {/* TODO: Replace with a more compact error UI; this is just to ensure the error state is shown */}
                            <ErrorOverlay active={surveysQuery.isError} onRetry={surveysQuery.refetch}>
                                {surveys ? (
                                    <Form.Field
                                        className={styles.selectWrapper}
                                        label="サーベイ選択"
                                        control={(...props) => (
                                            <Select
                                                {...props}
                                                disabled={selectedOrgId === ''}
                                                value={selectedSurvey?.surveyId}
                                                onChange={handleSurveyChange}
                                            >
                                                {surveys.length === 0 ? (
                                                    // TODO: Visual design for empty state
                                                    <option value="">サーベイがありません</option>
                                                ) : (
                                                    <option value="">--サーベイを選択してください--</option>
                                                )}
                                                {surveys.map((s) => (
                                                    <option key={s.surveyId} value={s.surveyId}>
                                                        {s.surveyName}
                                                    </option>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                ) : (
                                    selectedOrg && <Form.FieldSkeleton />
                                )}
                            </ErrorOverlay>
                        </Flex>
                    </Flex>
                </Section>

                {selectedOrg && surveysQuery.data && selectedSurvey && (
                    <SystemSurveys organization={selectedOrg} selectedSurvey={selectedSurvey} />
                )}
            </Page.Content>
        </Page.Root>
    );
}

function SystemSurveysRouteSkeleton() {
    return (
        <Page.Root>
            {/* The title is static, so it renders for real rather than as a placeholder. */}
            <Page.Header title="サーベイ分析" />

            <Page.Content>
                <Section pt="8" className={styles.selectionSection}>
                    <SectionHeader
                        title="クライアントと​サーベイ選択"
                        description="分析対象のクライアントとサーベイを選択します。クライアントを選ぶと、そのクライアントに紐づくサーベイが一覧に表示されます。ここで選んだサーベイの集計結果が、以降のすべてのセクションに反映されます。"
                    />

                    <Flex direction="column" gap="6">
                        <Flex gap="4">
                            <Form.FieldSkeleton className={styles.selectWrapper} />
                        </Flex>
                    </Flex>
                </Section>
            </Page.Content>
        </Page.Root>
    );
}

type SystemSurveysRouteProps = {
    organization: Organization;
    selectedSurvey: Survey;
};

function SystemSurveys({ organization, selectedSurvey }: SystemSurveysRouteProps) {
    const queryClient = useQueryClient();
    const { organizationId, surveyId } = Route.useSearch();
    /*
     * Picked rather than passed whole: this layout's search also carries the params of
     * the tab currently matched below it (e.g. `questionId`), which must not follow the
     * user onto the other tabs.
     */
    const search = { organizationId, surveyId };

    const publishMutation = useMutation({
        mutationFn: (isPublished: boolean) =>
            updatePublishStatus(organization.uuid, selectedSurvey.surveyId, { isPublished }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['surveys', organization.uuid] });
            showSuccessToast({ title: 'サーベイ', description: '公開設定を更新しました。' });
        },
        onError: () => {
            showErrorToast({ title: 'サーベイ', description: '公開設定の更新に失敗しました。' });
        },
    });

    return (
        <Section pt="8">
            <Pill
                variant={selectedSurvey.isPublished ? 'secondary' : 'tertiary'}
                className={classNames(styles.statusPill, hiddenInPrint)}
            >
                {selectedSurvey.isPublished ? '公開中' : '未公開'}
            </Pill>

            <SectionHeader
                title={organization.name}
                extras={
                    <Flex justify="end" className={hiddenInPrint}>
                        <UpdatePublishStatusDialog
                            survey={selectedSurvey}
                            isSubmitting={publishMutation.isPending}
                            onSubmit={() => publishMutation.mutateAsync(!selectedSurvey.isPublished)}
                        />
                    </Flex>
                }
                description={`サーベイ：${selectedSurvey.surveyName}`}
            />

            <Flex direction="column" gap="10" justify="start">
                <div className={classNames(styles.tabsWrapper, hiddenInPrint)}>
                    <RouteTabs
                        tabs={[
                            { to: '/system/surveys/summaries', search, label: '全社', icon: Building2 },
                            { to: '/system/surveys/categories', search, label: 'カテゴリー', icon: LayoutGrid },
                            { to: '/system/surveys/questions', search, label: '設問', icon: List },
                            { to: '/system/surveys/comments', search, label: 'コメント', icon: MessageCircleMore },
                        ]}
                    />
                </div>

                <Outlet />
            </Flex>
        </Section>
    );
}
